import {Pressable, Text, View} from "react-native"
import React, {useMemo} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component"
import AppHistory from "shared/history"
import Contact from "models/contact"
import ContactRelationship from "models/contact-relationship"
import ContactRelationshipsTable from "components/contact-relationships/table"
import Icon from "@kaspernj/api-maker/build/utils/icon"
import memo from "set-state-compare/build/memo"
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import Routes from "shared/routes"
import useI18n from "i18n-on-steroids/src/use-i18n.mjs"

/** @type {Array<string>} */
const relationshipsPreload = ["child.contact_type", "child.school_class.class_step", "parent.contact_type", "parent.school_class.class_step"]
/** @type {Record<string, Array<string>>} */
const relationshipsSelect = {
  ClassStep: ["id", "name"],
  Contact: ["firstName", "id", "lastName", "name", "pointsLastThirtyDays"],
  ContactRelationship: ["childId", "contactRelationshipTypeId", "createdAt", "id", "parentId", "updatedAt"],
  ContactType: ["id", "identifier"],
  SchoolClass: ["id"]
}

/** Admin contact relationships section. */
export default memo(shapeComponent(class ComponentsAdminContactsRelationshipsSection extends ShapeComponent {
  static defaultProps = {
    showDiagram: undefined
  }

  static propTypes = propTypesExact({
    contact: PropTypes.instanceOf(Contact).isRequired,
    showDiagram: PropTypes.bool
  })

  /** @returns {void} */
  setup() {
    const {t} = useI18n({namespace: "js.routes.admin.contacts.relationships"})
    const {contact} = this.p

    /** @type {import("@kaspernj/api-maker/build/collection.js").default} */
    this.parentsQuery = useMemo(
      () =>
        ContactRelationship
          .ransack({child_id_eq: contact.id()})
          .preload(relationshipsPreload)
          .select(relationshipsSelect),
      [contact.id()]
    )

    /** @type {import("@kaspernj/api-maker/build/collection.js").default} */
    this.childrenQuery = useMemo(
      () =>
        ContactRelationship
          .ransack({parent_id_eq: contact.id()})
          .preload(relationshipsPreload)
          .select(relationshipsSelect),
      [contact.id()]
    )

    this.contact = contact
    this.t = t
  }

  /** @returns {React.ReactNode} */
  render() {
    const {showDiagram} = this.p

    return (
      <View style={this.rootViewStyle ||= {width: "100%"}} testID="admin-contact-relationships-section">
        <ContactRelationshipsTable
          adminLayout
          collection={this.tt.parentsQuery}
          controls={this.parentsControls}
          header={Contact.humanAttributeName("parents")}
          identifier="contact-relationships-parents"
          relationDirection="parent"
          styles={this.parentsTableStyles ||= {container: {width: "100%", marginBottom: 24}}}
          testID="admin-contact-relationships-parents-table"
        />
        {showDiagram !== false &&
          <View
            style={this.relationshipDiagramContainerStyle ||= {alignItems: "center", marginBottom: 24}}
            testID="admin-contact-relationships-diagram"
          >
            <Text
              style={this.relationshipsLabelStyle ||= {color: "#4b5563", fontSize: 13, fontWeight: 600}}
              testID="admin-contact-relationships-parent-label"
            >
              {Contact.humanAttributeName("parentRelationships")}
            </Text>
            <Text style={this.arrowStyle ||= {color: "#111827", fontSize: 20, lineHeight: 20}} testID="admin-contact-relationships-parent-arrow">
              ↑
            </Text>
            <View style={this.verticalLineStyle ||= {backgroundColor: "#111827", height: 20, width: 2}} testID="admin-contact-relationships-parent-line" />
            <View
              style={this.contactCircleStyle ||= {
                alignItems: "center",
                borderColor: "#111827",
                borderRadius: 999,
                borderWidth: 3,
                minWidth: 220,
                paddingHorizontal: 30,
                paddingVertical: 20
              }}
              testID="admin-contact-relationships-contact-circle"
            >
              <Text
                style={this.contactCircleLabelStyle ||= {color: "#111827", fontSize: 28, fontWeight: 700, textAlign: "center"}}
                testID="admin-contact-relationships-contact-name"
              >
                {this.contact.name()}
              </Text>
            </View>
            <View
              style={this.verticalLineBottomStyle ||= {backgroundColor: "#111827", height: 20, width: 2}}
              testID="admin-contact-relationships-child-line"
            />
            <Text style={this.arrowBottomStyle ||= {color: "#111827", fontSize: 20, lineHeight: 20}} testID="admin-contact-relationships-child-arrow">
              ↓
            </Text>
            <Text
              style={this.relationshipsBottomLabelStyle ||= {color: "#4b5563", fontSize: 13, fontWeight: 600}}
              testID="admin-contact-relationships-child-label"
            >
              {this.t("activerecord.attributes.contact.children")}
            </Text>
          </View>
        }
        <ContactRelationshipsTable
          adminLayout
          collection={this.tt.childrenQuery}
          controls={this.childrenControls}
          header={Contact.humanAttributeName("children")}
          identifier="contact-relationships-children"
          relationDirection="child"
          styles={this.childrenTableStyles ||= {container: {width: "100%"}}}
          testID="admin-contact-relationships-children-table"
        />
      </View>
    )
  }

  /** @returns {React.ReactNode} */
  childrenControls = () => <>
    <Pressable
      onPress={this.onAddChildPress}
      testID="admin-contact-relationships-add-child"
    >
      <Icon name="pen" size={16} />
    </Pressable>
  </>

  /** @returns {void} */
  onAddChildPress = () => AppHistory.push(Routes.newAdminContactRelationshipPath({contact_relationship: {parent_id: this.contact.id()}}))
  /** @returns {void} */
  onAddParentPress = () => AppHistory.push(Routes.newAdminContactRelationshipPath({contact_relationship: {child_id: this.contact.id()}}))

  /** @returns {React.ReactNode} */
  parentsControls = () => <>
    <Pressable
      onPress={this.onAddParentPress}
      testID="admin-contact-relationships-add-parent"
    >
      <Icon name="pen" size={16} />
    </Pressable>
  </>
}))
